"use client";

import * as React from "react";

import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "../lib/utils";

function Table({
  className,
  containerClassName,
  ...props
}: React.ComponentProps<"table"> & { containerClassName?: string }) {
  return (
    <div
      data-slot="table-container"
      className={cn("relative w-full overflow-x-auto", containerClassName)}
    >
      <table
        data-slot="table"
        className={cn("w-full caption-bottom text-sm", className)}
        {...props}
      />
    </div>
  );
}

function TableHeader({ className, ...props }: React.ComponentProps<"thead">) {
  return (
    <thead
      data-slot="table-header"
      className={cn(
        "[&_tr]:border-border [&_tr]:border-b text-label-lg",
        className,
      )}
      {...props}
    />
  );
}

function TableBody({ className, ...props }: React.ComponentProps<"tbody">) {
  return (
    <tbody
      data-slot="table-body"
      className={cn("[&_tr:last-child]:border-0", className)}
      {...props}
    />
  );
}

function TableFooter({ className, ...props }: React.ComponentProps<"tfoot">) {
  return (
    <tfoot
      data-slot="table-footer"
      className={cn(
        "border-border-subtle border-t font-medium [&>tr]:last:border-b-0",
        className,
      )}
      {...props}
    />
  );
}

const tableRowVariants = cva(
  "hover:bg-surface-hover data-[state=selected]:bg-muted border-border-subtle border-b transition-colors",
  {
    variants: {
      variant: {
        default: "",
        success:
          "bg-success/8 [&_td:first-child]:border-l-2 [&_td:first-child]:border-l-success",
        warning:
          "bg-warning/8 [&_td:first-child]:border-l-2 [&_td:first-child]:border-l-warning",
        info: "bg-info/8 [&_td:first-child]:border-l-2 [&_td:first-child]:border-l-info",
        destructive:
          "bg-destructive/8 [&_td:first-child]:border-l-2 [&_td:first-child]:border-l-destructive",
        purple:
          "bg-purple/8 [&_td:first-child]:border-l-2 [&_td:first-child]:border-l-purple",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  },
);

function TableRow({
  className,
  variant,
  onClick,
  ...props
}: React.ComponentProps<"tr"> & VariantProps<typeof tableRowVariants>) {
  return (
    <tr
      data-slot="table-row"
      className={cn(
        tableRowVariants({ variant }),
        onClick && "cursor-pointer",
        className,
      )}
      onClick={onClick}
      {...props}
    />
  );
}

const tableHeadVariants = cva(
  "h-10 px-2 first:pl-4 last:pr-4 align-middle text-left font-medium whitespace-nowrap [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-[2px]",
  {
    variants: {
      align: {
        left: "text-left",
        center: "text-center",
        right: "text-right",
      },
      wide: {
        true: "w-full max-w-px overflow-ellipsis overflow-hidden",
      },
      narrow: {
        true: "w-0 whitespace-nowrap",
      },
      numeric: {
        true: "tabular-nums text-right",
      },
    },
    defaultVariants: {
      align: "left",
      wide: false,
      narrow: false,
      numeric: false,
    },
  },
);

function TableHead({
  className,
  align,
  wide,
  narrow,
  numeric,
  ...props
}: React.ComponentProps<"th"> & VariantProps<typeof tableHeadVariants>) {
  return (
    <th
      data-slot="table-head"
      className={cn(
        tableHeadVariants({ align, wide, narrow, numeric }),
        className,
      )}
      {...props}
    />
  );
}

const tableCellVariants = cva(
  "h-13 px-2 first:pl-4 last:pr-4 align-middle whitespace-nowrap [&:has([role=checkbox])]:pr-0 *:[[role=checkbox]]:translate-y-[2px]",
  {
    variants: {
      align: {
        left: "text-left",
        center: "text-center",
        right: "text-right",
      },
      wide: {
        true: "w-full max-w-px overflow-ellipsis overflow-hidden",
      },
      narrow: {
        true: "w-0 whitespace-nowrap",
      },
      secondary: {
        true: "text-text-secondary",
      },
      numeric: {
        true: "tabular-nums text-right",
      },
    },
    defaultVariants: {
      align: "left",
      wide: false,
      narrow: false,
      secondary: false,
      numeric: false,
    },
  },
);

function TableCell({
  className,
  align,
  wide,
  narrow,
  secondary,
  numeric,
  ...props
}: React.ComponentProps<"td"> & VariantProps<typeof tableCellVariants>) {
  return (
    <td
      data-slot="table-cell"
      className={cn(
        tableCellVariants({ align, secondary, wide, narrow, numeric }),
        className,
      )}
      {...props}
    />
  );
}

function TableCaption({
  className,
  ...props
}: React.ComponentProps<"caption">) {
  return (
    <caption
      data-slot="table-caption"
      className={cn("text-muted-foreground mt-4 text-sm", className)}
      {...props}
    />
  );
}

export {
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
};
